import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, CheckCircle2, AlertTriangle, Save, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELDS = [
  { key: 'COMPANY_NAME', label: 'Επωνυμία εταιρείας', placeholder: 'π.χ. COURTPLAN Μ.ΙΚΕ' },
  { key: 'COMPANY_VAT_NUMBER', label: 'ΑΦΜ', placeholder: 'π.χ. 800123456' },
  { key: 'COMPANY_ADDRESS', label: 'Διεύθυνση' },
  { key: 'COMPANY_CITY', label: 'Πόλη' },
  { key: 'COMPANY_POSTCODE', label: 'Τ.Κ.' },
  { key: 'COMPANY_VAT_RATE', label: 'Συντελεστής ΦΠΑ (%)', placeholder: '24' },
  { divider: true, title: 'myDATA API' },
  { key: 'MYDATA_USERNAME', label: 'Username Ειδικού Συνδρομητή', placeholder: 'από mydataapi.aade.gr' },
  { key: 'MYDATA_SUBSCRIPTION_KEY', label: 'Subscription Key', secret: true, placeholder: 'Ocp-Apim-Subscription-Key' },
  { key: 'MYDATA_BASE_URL', label: 'Base URL', options: [
      { value: 'https://mydatapi-dev.aade.gr/myDATA/RestApi', label: 'Sandbox (dev)' },
      { value: 'https://mydatapi.aade.gr/myDATA/RestApi', label: 'Production' },
  ] },
  { key: 'MYDATA_ENABLED', label: 'Ενεργοποίηση υποβολής myDATA', toggle: true },
];

export default function MyDataSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});
  const [secretVisible, setSecretVisible] = useState(false);
  const [effectiveEnabled, setEffectiveEnabled] = useState(false);
  const [secretIsSet, setSecretIsSet] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/settings/mydata`, { withCredentials: true });
      setEffectiveEnabled(!!data._effective_enabled);
      setSecretIsSet(!!data.MYDATA_SUBSCRIPTION_KEY_set);
      const v = {};
      FIELDS.forEach((f) => {
        if (f.divider) return;
        v[f.key] = data[f.key] ?? '';
      });
      setValues(v);
    } catch (err) {
      toast.error('Αποτυχία φόρτωσης ρυθμίσεων');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Don't send the masked secret back (it has ••••)
      const payload = { ...values };
      if (payload.MYDATA_SUBSCRIPTION_KEY && payload.MYDATA_SUBSCRIPTION_KEY.includes('••••')) {
        delete payload.MYDATA_SUBSCRIPTION_KEY;
      }
      await axios.put(`${API}/admin/settings/mydata`, payload, { withCredentials: true });
      toast.success('Οι ρυθμίσεις αποθηκεύτηκαν');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Αποτυχία αποθήκευσης');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="mydata-settings">
      <div className="p-4 border-b border-slate-200 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
          <Shield className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="font-semibold text-slate-900">myDATA (ΑΑΔΕ) · Στοιχεία εταιρείας</h2>
          <p className="text-xs text-slate-500">Ρύθμιση σύνδεσης για την αυτόματη υποβολή τιμολογίων</p>
        </div>
        {effectiveEnabled ? (
          <span className="flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded">
            <CheckCircle2 className="w-3 h-3" /> Ενεργό
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded">
            <AlertTriangle className="w-3 h-3" /> Ανενεργό
          </span>
        )}
      </div>

      {loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Φόρτωση...
        </div>
      ) : (
        <div className="p-5 space-y-4">
          {FIELDS.map((f, idx) => {
            if (f.divider) {
              return (
                <div key={idx} className="pt-2 border-t border-slate-100">
                  <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium">{f.title}</h3>
                </div>
              );
            }
            if (f.toggle) {
              const on = values[f.key] === 'true';
              return (
                <label key={f.key} className="flex items-center justify-between py-2 cursor-pointer" data-testid={`mydata-field-${f.key}`}>
                  <div>
                    <div className="text-sm font-medium text-slate-800">{f.label}</div>
                    <div className="text-xs text-slate-500">Υποβολή κάθε τιμολογίου στην ΑΑΔΕ αμέσως μετά την έκδοση</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChange(f.key, on ? 'false' : 'true')}
                    className={`relative w-12 h-6 rounded-full transition-colors ${on ? 'bg-green-600' : 'bg-slate-300'}`}
                    data-testid={`mydata-toggle-${f.key}`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all ${on ? 'left-6' : 'left-0.5'}`} />
                  </button>
                </label>
              );
            }
            if (f.options) {
              return (
                <div key={f.key} data-testid={`mydata-field-${f.key}`}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                  <Select value={values[f.key] || undefined} onValueChange={(v) => handleChange(f.key, v)}>
                    <SelectTrigger className="w-full h-10 border-slate-300 rounded-md text-sm bg-white focus:ring-0">
                      <SelectValue placeholder="Επιλέξτε..." />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.filter((opt) => opt.value !== '').map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} className="text-sm">{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            return (
              <div key={f.key} data-testid={`mydata-field-${f.key}`}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {f.label}
                  {f.secret && secretIsSet && !values[f.key]?.includes('••••') && (
                    <span className="ml-2 text-xs text-green-600">(νέο κλειδί θα αντικαταστήσει το τρέχον)</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={f.secret && !secretVisible ? 'password' : 'text'}
                    value={values[f.key] || ''}
                    onChange={(e) => handleChange(f.key, e.target.value)}
                    placeholder={f.placeholder || ''}
                    className="w-full h-10 px-3 pr-10 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-red-700 focus:border-red-700"
                  />
                  {f.secret && (
                    <button
                      type="button"
                      onClick={() => setSecretVisible(!secretVisible)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                    >
                      {secretVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="mydata-save-btn"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
            </Button>
          </div>

          <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
            ℹ️ Όταν είναι ενεργό, κάθε νέο τιμολόγιο υποβάλλεται αυτόματα στο myDATA REST API της ΑΑΔΕ και λαμβάνει ΜΑΡΚ.
          </div>
        </div>
      )}
    </div>
  );
}
